/**
 * JSON diffing utilities for workflow change proposals
 */
import { diff, Diff } from 'deep-diff';
import type { DiffResult, DiffSummary, DiffStats } from '@/types/proposal';

/**
 * Check if a diff path should be filtered out (e.g., metadata which is auto-preserved)
 */
function shouldFilterPath(path: string): boolean {
  // Filter out metadata paths since we auto-preserve them during apply
  // This includes paths like "definition.triggers.Recurrence.metadata" or "definition.actions.Send_email.metadata.position"
  const parts = path.split('.');
  return parts.includes('metadata');
}

/**
 * Find the line number where a path's key appears in a JSON string
 * Returns the line number (1-indexed) or undefined if not found
 */
function findLineNumberForPath(jsonString: string, pathArray: (string | number)[]): number | undefined {
  if (!jsonString) {
    return undefined;
  }

  if (!pathArray || pathArray.length === 0) {
    return undefined;
  }

  // Build a regex pattern to find the final key in the path
  // For paths like ["definition", "actions", "Send_email", "inputs"], we want to find "inputs"
  const finalKey = pathArray[pathArray.length - 1];

  if (finalKey === undefined) {
    return undefined;
  }

  // For array indices, we need to find the parent key and count array elements
  if (typeof finalKey === 'number') {
    // For array paths, use the parent key
    if (pathArray.length >= 2) {
      const parentKey = pathArray[pathArray.length - 2];
      if (typeof parentKey === 'string') {
        return findKeyLineNumber(jsonString, parentKey, pathArray.slice(0, -1));
      }
    }
    return undefined;
  }

  return findKeyLineNumber(jsonString, String(finalKey), pathArray);
}

/**
 * Find the line number where a specific key appears, considering the path context
 */
function findKeyLineNumber(jsonString: string, key: string, pathArray: (string | number)[]): number | undefined {
  // Normalize line endings (handle Windows \r\n)
  const normalizedJson = jsonString.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedJson.split('\n');

  // Create a pattern that matches the key as a JSON property
  const keyPattern = new RegExp(`"${escapeRegExp(key)}"\\s*:`);

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    if (!line) continue;

    // Check if this line contains our key
    if (keyPattern.test(line)) {
      // For simple cases, check if the path context matches
      // by looking at preceding keys in the expected order
      if (isPathContextMatch(lines, lineIndex, pathArray)) {
        return lineIndex + 1; // Convert to 1-indexed
      }
    }
  }

  // Fallback: find the first occurrence of the key
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    if (line && keyPattern.test(line)) {
      return lineIndex + 1;
    }
  }

  return undefined;
}

/**
 * Check if the lines preceding lineIndex contain the expected path context
 */
function isPathContextMatch(lines: string[], lineIndex: number, pathArray: (string | number)[]): boolean {
  if (pathArray.length <= 1) {
    return true;
  }

  // Look backwards through the path to verify context
  const pathWithoutLast = pathArray.slice(0, -1);
  let searchIndex = lineIndex - 1;

  for (let i = pathWithoutLast.length - 1; i >= 0; i--) {
    const pathPart = pathWithoutLast[i];
    if (typeof pathPart === 'number') {
      continue; // Skip array indices
    }

    const keyPattern = new RegExp(`"${escapeRegExp(String(pathPart))}"\\s*:`);
    let found = false;

    while (searchIndex >= 0) {
      const searchLine = lines[searchIndex];
      if (searchLine && keyPattern.test(searchLine)) {
        found = true;
        searchIndex--;
        break;
      }
      searchIndex--;
    }

    if (!found) {
      return false;
    }
  }

  return true;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Compute the diff between two objects
 * @param baseline The baseline object
 * @param proposed The proposed object
 * @param baselineJson Optional JSON string for baseline (used to compute line numbers for removed items)
 * @param proposedJson Optional JSON string for proposed (used to compute line numbers for added/modified items)
 */
export function computeJsonDiff(
  baseline: unknown,
  proposed: unknown,
  baselineJson?: string,
  proposedJson?: string
): DiffResult {
  // Auto-format single-line JSON strings for proper line number calculation
  let formattedBaselineJson = baselineJson;
  let formattedProposedJson = proposedJson;

  if (baselineJson && baselineJson.split('\n').length === 1) {
    try {
      formattedBaselineJson = JSON.stringify(JSON.parse(baselineJson), null, 2);
    } catch {
      // Keep original if parsing fails
    }
  }
  if (proposedJson && proposedJson.split('\n').length === 1) {
    try {
      formattedProposedJson = JSON.stringify(JSON.parse(proposedJson), null, 2);
    } catch {
      // Keep original if parsing fails
    }
  }

  const differences = diff(baseline, proposed);

  if (!differences || differences.length === 0) {
    return {
      hasChanges: false,
      summary: [],
      stats: { added: 0, removed: 0, modified: 0, total: 0 },
    };
  }

  // Map differences to summary and filter out metadata paths
  const summary: DiffSummary[] = differences
    .map((d: Diff<unknown, unknown>, index: number) => {
      const path = d.path?.join('.') || 'root';
      const type = mapDiffKind(d.kind);

      // Calculate line number based on change type (using formatted JSON strings)
      let lineNumber: number | undefined;
      if (d.path) {
        if (type === 'removed' && formattedBaselineJson) {
          // For removed items, find line in baseline
          lineNumber = findLineNumberForPath(formattedBaselineJson, d.path);
        } else if ((type === 'added' || type === 'modified') && formattedProposedJson) {
          // For added/modified items, find line in proposed
          lineNumber = findLineNumberForPath(formattedProposedJson, d.path);
        }
      }

      return {
        id: `change-${index}-${Date.now()}`,
        path,
        type,
        oldValue: 'lhs' in d ? d.lhs : undefined,
        newValue: 'rhs' in d ? d.rhs : undefined,
        status: 'pending' as const,
        lineNumber,
      };
    })
    .filter((item) => !shouldFilterPath(item.path));

  // If all differences were metadata, return no changes
  if (summary.length === 0) {
    return {
      hasChanges: false,
      summary: [],
      stats: { added: 0, removed: 0, modified: 0, total: 0 },
    };
  }

  const stats = computeStats(summary);

  return { hasChanges: true, summary, stats };
}

/**
 * Map deep-diff kind to our summary type
 */
function mapDiffKind(kind: string): 'added' | 'removed' | 'modified' {
  switch (kind) {
    case 'N': // New
      return 'added';
    case 'D': // Deleted
      return 'removed';
    case 'E': // Edited
    case 'A': // Array change
    default:
      return 'modified';
  }
}

/**
 * Compute statistics from diff summary
 */
function computeStats(summary: DiffSummary[]): DiffStats {
  const stats = { added: 0, removed: 0, modified: 0, total: 0 };

  for (const item of summary) {
    stats[item.type]++;
    stats.total++;
  }

  return stats;
}

/**
 * Validation result for proposed JSON
 * - valid: true if JSON is syntactically correct and structurally valid
 * - parseError: true if JSON.parse() failed (syntax error)
 * - error: human-readable error message
 * - parsed: parsed object if parsing succeeded
 */
export interface JsonValidationResult {
  valid: boolean;
  parseError?: boolean;  // true = JSON syntax error, false/undefined = structural error
  error?: string;
  parsed?: unknown;
}

/**
 * Validate proposed JSON string for a given type
 */
export function validateProposedJson(
  json: string,
  type: 'workflow' | 'node'
): JsonValidationResult {
  try {
    const parsed = JSON.parse(json);

    if (type === 'workflow') {
      if (!parsed.definition) {
        return { valid: false, error: 'Missing "definition" property in workflow', parsed };
      }
      if (!parsed.definition.triggers && !parsed.definition.actions) {
        return { valid: false, error: 'Workflow definition must have triggers or actions', parsed };
      }
    }

    // For 'node' type (scoped edits), no structural validation needed —
    // the jsonPath identifies the target, not the JSON shape.
    // Valid as long as it parses as JSON.

    return { valid: true, parsed };
  } catch (e) {
    const error = e instanceof Error ? e.message : 'Unknown parse error';
    return { valid: false, parseError: true, error: `Invalid JSON: ${error}` };
  }
}

/**
 * Group diff summaries by their top-level path
 * Useful for collapsible sections in large diffs
 */
export function groupDiffByPath(summary: DiffSummary[]): Map<string, DiffSummary[]> {
  const groups = new Map<string, DiffSummary[]>();

  for (const item of summary) {
    // Get top-level path (e.g., "actions" from "actions.Send_email.inputs.to")
    const topLevel = item.path.split('.')[0] || 'root';

    if (!groups.has(topLevel)) {
      groups.set(topLevel, []);
    }
    groups.get(topLevel)!.push(item);
  }

  return groups;
}

/**
 * Format a path for display (e.g., "actions.Send_email" -> "Send_email (action)")
 */
export function formatPathForDisplay(path: string): string {
  const parts = path.split('.');

  if (parts.length === 0) return path;

  // Handle workflow structure paths
  if (parts[0] === 'definition') {
    parts.shift(); // Remove "definition" prefix
  }

  if (parts[0] === 'actions' && parts[1]) {
    return `${parts[1]} (action)${parts.length > 2 ? '.' + parts.slice(2).join('.') : ''}`;
  }

  if (parts[0] === 'triggers' && parts[1]) {
    return `${parts[1]} (trigger)${parts.length > 2 ? '.' + parts.slice(2).join('.') : ''}`;
  }

  return path;
}

/**
 * Check if a diff is considered "large" based on thresholds
 */
export function isLargeDiff(diff: DiffResult, jsonSize: number): boolean {
  const LARGE_CHANGE_COUNT = 50;
  const LARGE_SIZE_BYTES = 10240; // 10KB

  return diff.stats.total > LARGE_CHANGE_COUNT || jsonSize > LARGE_SIZE_BYTES;
}

/**
 * Create a human-readable summary of the diff
 */
export function createDiffSummaryText(diff: DiffResult): string {
  if (!diff.hasChanges) {
    return 'No changes detected';
  }

  const parts: string[] = [];

  if (diff.stats.added > 0) {
    parts.push(`${diff.stats.added} addition${diff.stats.added > 1 ? 's' : ''}`);
  }
  if (diff.stats.removed > 0) {
    parts.push(`${diff.stats.removed} removal${diff.stats.removed > 1 ? 's' : ''}`);
  }
  if (diff.stats.modified > 0) {
    parts.push(`${diff.stats.modified} modification${diff.stats.modified > 1 ? 's' : ''}`);
  }

  return parts.join(', ');
}
